import { graphql } from 'gatsby'
import React, { useContext } from 'react'
import BreadCrumb from '../components/shared/BreadCrumb'
import { SiteLink } from '../components/shared/SiteLink'
import { LayoutContext } from '../contexts'
import { useGlobal } from '../hooks/useGlobal'
import { desktopVW, mobileVW } from '../styles'
import theme from '../styles/theme'
import styled from 'styled-components'
import AmbassadorItem from '../components/shared/AmbassadorItem'
import BlockRenderer from '../components/shared/BlockRenderer'
import SEO from '../components/SEO'

const { breakPoints, colors } = theme

const Ambassador = ({
  data: {
    contentfulPage: {
      name,
      subTitle,
      seoTitle,
      seoImage,
      seoDescription,
      componentBlocks,
      layoutType,
    },
    ambassadorList: { ambassadors },
    allTrustPilotSummary: {
      nodes: [
        {
          score: { trustpilotStars },
          totalNumberOfReviews,
        },
      ],
    },
  },
  location,
}) => {
  const { node_locale } = useContext(LayoutContext)

  const { layout: dictionary } = useGlobal(['layout'], node_locale)

  const orderedAmbassadors = ambassadors.sort((a, b) => {
    return a.ambassadorOrder - b.ambassadorOrder
  })

  return (
    <>
      <SEO
        isPage
        location={location}
        title={seoTitle}
        description={seoDescription}
        image={seoImage?.file?.url}
        noIndex={layoutType && layoutType.noIndex}
        referrer={location.state?.referrer}
      />
      <Header>
        <BreadCrumb>
          {dictionary?.home && <SiteLink>{dictionary.home}</SiteLink>} / {name}
        </BreadCrumb>
      </Header>
      <Inner>
        <Title>{name}</Title>
        {subTitle && <p>{subTitle}</p>}
      </Inner>
      <Wrapper>
        {orderedAmbassadors.map((item) => (
          <AmbassadorItem data={item} key={item.id} />
        ))}
      </Wrapper>
      <div>
        <BlockRenderer
          blocks={componentBlocks}
          rating={{ totalNumberOfReviews, trustpilotStars }}
          location={location}
        />
      </div>
    </>
  )
}

const Header = styled.div`
  margin: ${mobileVW(10)} auto;
  font-size: ${mobileVW(10)};
  width: 90%;
  color: ${colors.grey};

  @media screen and (min-width: ${breakPoints.desktop}) {
    max-width: ${desktopVW(1200, 1440)};
    font-size: ${desktopVW(12, 1440)};
    margin: ${desktopVW(40, 1440)} auto;
  }
`

const Inner = styled.div`
  display: flex;
  flex-flow: column;
  text-align: center;
  margin: 0 auto;
  width: 90%;
  color: ${colors.grey};
  font-size: ${mobileVW(14)};
  line-height: 1.618;

  @media screen and (min-width: ${breakPoints.desktop}) {
    max-width: ${desktopVW(1200, 1440)};
    font-size: ${desktopVW(14, 1440)};
  }
`

const Title = styled.h1`
  font-size: ${mobileVW(24)};
  color: ${colors.black};

  @media screen and (min-width: ${breakPoints.desktop}) {
    font-size: ${desktopVW(46, 1440)};
  }
`

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${mobileVW(40)};
  width: 90%;
  margin: ${mobileVW(30)} auto;

  @media screen and (min-width: ${breakPoints.desktop}) {
    max-width: ${desktopVW(1200, 1440)};
    grid-template-columns: repeat(3, 1fr);
    grid-gap: ${desktopVW(20, 1440)};
    margin-top: ${desktopVW(50, 1440)};
  }
`

export const pageQuery = graphql`
  query AmbassadorOverview($id: String!, $node_locale: String!) {
    contentfulPage(id: { eq: $id }) {
      name
      subTitle
      seoTitle
      seoDescription
      template
      seoImage {
        file {
          url
        }
      }
      layoutType {
        name
        noIndex
      }
      componentBlocks {
        ...pageBlocks
      }
    }

    ambassadorList: allContentfulPage(
      filter: {
        template: { eq: "Ambassador" }
        node_locale: { eq: $node_locale }
      }
    ) {
      ambassadors: nodes {
        ...ambassadorItem
      }
    }

    allTrustPilotSummary {
      ...pageRatings
    }
  }
`

export default Ambassador
